import React, { useState, useEffect, useRef } from 'react';
import useEscapeKey from '../../hooks/useEscapeKey';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { QRCodeSVG } from 'qrcode.react';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../common/ConfirmDialog';
import { useAuthStore } from '../../store/authStore';
import { extensionHasActiveCall } from '../../utils/callSafety';
import {
  Plus, Search, Trash2, Phone, Eye, EyeOff, X, ChevronLeft,
  Voicemail, PhoneForwarded, Mic, ToggleLeft, Settings,
  Radio, CheckCircle, XCircle, AlertCircle, Clock, Coffee,
  Briefcase, Copy, RotateCcw, Wifi, WifiOff, QrCode, User,
  Shield, Monitor, ChevronDown, Download, Smartphone,
} from 'lucide-react';

/* ─── Constants ─────────────────────────────────────────────── */
const STATUS_COLORS = {
  'Not in use': 'online', 'In use': 'busy', 'Unavailable': 'offline',
  'Ringing': 'ringing', 'DND': 'dnd', 'Unknown': 'offline',
};
const STATUS_LABELS = {
  'Not in use': 'Registrado', 'In use': 'En llamada', 'Unavailable': 'Desconectado',
  'Ringing': 'Timbrando', 'Busy': 'Ocupado', 'DND': 'No molestar',
  'Unknown': 'Desconocido', 'unknown': 'Desconocido',
};
const statusLabel = (s) => STATUS_LABELS[s] || s || 'Desconocido';

const PRESENCE_OPTIONS = [
  { value: 'available', label: 'Disponible', color: 'text-green-400', icon: CheckCircle },
  { value: 'away', label: 'Ausente', color: 'text-yellow-400', icon: Clock },
  { value: 'dnd', label: 'No molestar', color: 'text-red-400', icon: XCircle },
  { value: 'lunch', label: 'Almuerzo', color: 'text-orange-400', icon: Coffee },
  { value: 'business_trip', label: 'Viaje de negocios', color: 'text-blue-400', icon: Briefcase },
  { value: 'custom', label: 'Personalizado', color: 'text-purple-400', icon: AlertCircle },
];

const TABS = [
  { id: 'general', label: 'General' },
  { id: 'forwarding', label: 'Ruteo de llamadas' },
  { id: 'voicemail', label: 'Buzón de voz' },
  { id: 'device', label: 'Teléfono IP' },
  { id: 'blf', label: 'BLF' },
  { id: 'vista', label: 'Vista' },
  { id: 'options', label: 'Opciones' },
];

const VISTA_COLUMNS = [
  { key: 'show_my_calls', label: 'Mostrar mis llamadas', camel: 'showMyCalls', desc: 'Permite que otros miembros del departamento vean las llamadas activas de esta extensión.' },
  { key: 'barge_listen_whisper', label: 'Irrumpir / Escuchar / Susurrar', camel: 'bargeListenWhisper', desc: 'Permite que supervisores intervengan, escuchen o susurren al agente.' },
  { key: 'view_presence', label: 'Ver presencia', camel: 'viewPresence', desc: 'Puede ver el estado de presencia de otros en el departamento.' },
  { key: 'view_calls', label: 'Ver llamadas', camel: 'viewCalls', desc: 'Puede ver las llamadas activas de otros miembros.' },
  { key: 'call_operations', label: 'Operación de llamadas', camel: 'callOperations', desc: 'Puede transferir, parquear o colgar llamadas de otros.' },
  { key: 'user_operations', label: 'Operaciones de usuario', camel: 'userOperations', desc: 'Puede cambiar el estado de disponibilidad de otros usuarios.' },
];

/* ─── Small helpers ─────────────────────────────────────────── */
function Toggle({ checked, onChange, label }) {
  // Checkbox real oculto en vez de un <div onClick> simulando el switch: el
  // <label> ahora envuelve un control DE VERDAD (mismo click-to-toggle nativo
  // del label) y de paso suma teclado (Tab+Espacio) y lector de pantalla gratis.
  return (
    <label className="flex items-center gap-3 cursor-pointer select-none">
      <input type="checkbox" className="sr-only" checked={!!checked}
        onChange={e => onChange(e.target.checked)} />
      <div className="relative">
        <div className={`w-10 h-5 rounded-full transition-colors ${checked ? 'bg-blue-600' : 'bg-gray-700'}`} />
        <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-5' : ''}`} />
      </div>
      {label && <span className="text-sm text-gray-300">{label}</span>}
    </label>
  );
}

function Field({ label, htmlFor, children, className = '' }) {
  // El control se ANIDA dentro de <label> (asociación implícita nativa, funciona
  // aunque haya un <div> envolvente entre medio) en vez de ir como hermano sin
  // vincular. El texto va en un <span> aparte para que "text-xs text-gray-400"
  // no se herede hacia el control -- misma cadena de herencia de fuente que antes.
  // htmlFor es opcional: el detector de "placeholder-only field" exige el
  // vínculo explícito id/htmlFor además del anidado; el caller pone el mismo
  // valor como id en su input.
  return (
    <div className={className}>
      <label className="block mb-1.5" htmlFor={htmlFor}>
        <span className="text-xs text-gray-400">{label}</span>
        {children}
      </label>
    </div>
  );
}

function SipCredRow({ label, value }) {
  const [show, setShow] = useState(false);
  const copy = () => { navigator.clipboard.writeText(value); toast.success('Copiado'); };
  return (
    <div className="flex items-center justify-between p-2.5 bg-gray-800/60 rounded-lg">
      <span className="text-xs text-muted w-32 shrink-0">{label}</span>
      <span className="text-sm font-mono text-gray-200 flex-1 truncate">{show ? value : '••••••••'}</span>
      <div className="flex gap-1 ml-2">
        <button onClick={() => setShow(s => !s)} aria-label={show ? `Ocultar ${label}` : `Mostrar ${label}`} className="p-1 text-muted hover:text-blue-400">{show ? <EyeOff size={13} /> : <Eye size={13} />}</button>
        <button onClick={copy} aria-label={`Copiar ${label}`} className="p-1 text-muted hover:text-blue-400"><Copy size={13} /></button>
      </div>
    </div>
  );
}

function InfoTooltip({ text }) {
  const [show, setShow] = useState(false);
  return (
    <span className="relative inline-flex">
      <button type="button" aria-label="Más información" className="text-muted hover:text-gray-400 ml-1"
        onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
        <AlertCircle size={12} />
      </button>
      {show && (
        <span className="absolute z-50 left-5 top-0 w-56 p-2.5 bg-gray-800 border border-gray-700 rounded-lg text-xs text-gray-300 shadow-xl pointer-events-none">
          {text}
        </span>
      )}
    </span>
  );
}

/* ─── QR Provisioning Modal ─────────────────────────────────── */
function CopyBtn({ value, className = '' }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button onClick={copy} aria-label={copied ? 'Copiado' : 'Copiar'} className={`p-1 rounded transition-colors ${copied ? 'text-green-400' : 'text-muted hover:text-gray-300'} ${className}`}>
      {copied ? <CheckCircle size={13} /> : <Copy size={13} />}
    </button>
  );
}

function QrProvisionModal({ data, onClose, qrRef }) {
  const [activeTab, setActiveTab] = useState('mobile'); // 'mobile' | 'desktop'
  useEscapeKey(true, onClose); // el modal solo existe montado mientras está abierto

  const { provisionUrl, desktop, token, expiresIn } = data;

  const downloadSvg = () => {
    const svg = qrRef.current?.querySelector('svg');
    if (!svg) return;
    const blob = new Blob([svg.outerHTML], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `provision-ext-${desktop.username}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const hoursLeft = Math.round((expiresIn || 86400) / 3600);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm">
      <div role="dialog" aria-modal="true" className="bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-teal-600/20 rounded-lg flex items-center justify-center">
              <QrCode size={16} className="text-teal-400" />
            </div>
            <div>
              <p className="text-sm font-semibold text-white">Aprovisionamiento</p>
              <p className="text-xs text-muted">Extensión {desktop.username} · expira en {hoursLeft}h</p>
            </div>
          </div>
          <button onClick={onClose} aria-label="Cerrar" className="p-1.5 text-muted hover:text-white rounded-lg hover:bg-gray-800 transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Tab selector */}
        <div className="flex border-b border-gray-800">
          {[
            { id: 'mobile', label: 'App Móvil', icon: Smartphone },
            { id: 'desktop', label: 'App Escritorio', icon: Monitor },
          ].map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => setActiveTab(id)}
              className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === id
                  ? 'border-teal-500 text-teal-400 bg-teal-600/5'
                  : 'border-transparent text-muted hover:text-gray-300'
              }`}>
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>

        {/* ── App Móvil ── */}
        {activeTab === 'mobile' && (
          <div>
            {/* Instrucciones Linphone */}
            <div className="px-5 py-3 bg-blue-900/20 border-b border-blue-800/30">
              <p className="text-xs font-semibold text-blue-300 mb-1.5">Cómo usar en Linphone</p>
              <ol className="text-xs text-gray-400 space-y-1 list-decimal list-inside">
                <li>Abre <strong className="text-gray-200">Linphone</strong> → menú ☰ → <strong className="text-gray-200">Configuración</strong></li>
                <li>Selecciona <strong className="text-gray-200">Aprovisionamiento remoto</strong></li>
                <li>Pega la URL o escanea este QR</li>
                <li>Toca <strong className="text-gray-200">Aplicar</strong> y reinicia la app</li>
              </ol>
              <p className="text-xs text-amber-400/80 mt-2 flex items-start gap-1">
                <AlertCircle size={11} className="mt-0.5 shrink-0" />
                Enlace de <strong>un solo uso</strong> · expira en {hoursLeft} h
              </p>
            </div>

            {/* QR code */}
            <div ref={qrRef} className="flex justify-center p-8 bg-white">
              <QRCodeSVG
                value={provisionUrl}
                size={224}
                level="M"
                includeMargin={false}
                imageSettings={{
                  src: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'%3E%3Crect width='32' height='32' rx='8' fill='%232563eb'/%3E%3Cpath d='M8.8 14.4c1.87 3.73 5.07 6.8 8.8 8.8l2.93-2.93c.4-.4.93-.53 1.33-.27 1.47.53 3.07.8 4.8.8.8 0 1.33.53 1.33 1.33V26c0 .8-.53 1.33-1.33 1.33C13.47 27.33 4 17.87 4 5.33 4 4.53 4.53 4 5.33 4H9.8c.8 0 1.33.53 1.33 1.33 0 1.73.27 3.33.8 4.8.13.4 0 .93-.27 1.33L8.8 14.4z' fill='white'/%3E%3C/svg%3E",
                  x: undefined, y: undefined, height: 40, width: 40, excavate: true,
                }}
              />
            </div>

            <div className="px-5 pb-5 space-y-3">
              <div className="flex items-center gap-2 p-2.5 bg-gray-800/50 rounded-lg">
                <span className="text-xs text-muted shrink-0">URL</span>
                <span className="text-xs font-mono text-gray-300 flex-1 truncate">{provisionUrl}</span>
                <CopyBtn value={provisionUrl} />
              </div>
              <button onClick={downloadSvg}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600/15 hover:bg-blue-600/25 border border-blue-600/30 text-blue-400 rounded-xl text-sm font-medium transition-colors">
                <Download size={14} /> Descargar QR como imagen
              </button>
            </div>
          </div>
        )}

        {/* ── App Escritorio / Windows ── */}
        {activeTab === 'desktop' && (
          <div className="px-5 py-5 space-y-4">
            <div className="p-3 bg-blue-900/20 border border-blue-800/30 rounded-xl mb-2">
              <p className="text-xs font-semibold text-blue-300 mb-1">Linphone Windows / Mac</p>
              <p className="text-xs text-gray-400">
                Configuración → Cuenta SIP → Agregar cuenta → ingresa los datos a continuación.
                O usa <strong className="text-gray-200">Aprovisionamiento remoto</strong> con la URL del QR.
              </p>
            </div>

            {[
              { label: 'Servidor SIP (dominio)', value: desktop.url.replace('https://', ''), mono: true },
              { label: 'Usuario / Extensión', value: desktop.username, mono: true },
              { label: 'Contraseña', value: desktop.password, mono: true, secret: true },
            ].map(({ label, value, mono, secret }) => (
              <DesktopCredField key={label} label={label} value={value} mono={mono} secret={secret} />
            ))}

            <div className="pt-3 border-t border-gray-800 space-y-2">
              <p className="text-xs text-muted font-medium uppercase tracking-wide">Configuración avanzada</p>
              {[
                ['Transporte', data.transport || 'WSS'],
                ['Puerto SIP', String(data.sip_port || 5061)],
                ['WebSocket path', data.ws || '/ws/sip'],
                ['Codecs', (data.codecs || []).join(', ')],
              ].map(([k, v]) => (
                <div key={k} className="flex items-center justify-between py-1.5">
                  <span className="text-xs text-muted">{k}</span>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-mono text-gray-300">{v}</span>
                    <CopyBtn value={v} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

function DesktopCredField({ label, value, mono, secret }) {
  const [show, setShow] = useState(!secret);
  return (
    <div>
      {/* span, no label: es una fila de credencial de SOLO LECTURA (un span, no
          un input) -- no hay control editable que este texto pueda nombrar */}
      <span className="block text-xs text-muted mb-1.5">{label}</span>
      <div className="flex items-center gap-2 px-3 py-2.5 bg-gray-800/60 border border-gray-700 rounded-xl">
        <span className={`flex-1 text-sm text-gray-100 ${mono ? 'font-mono' : ''} ${secret && !show ? 'tracking-widest text-muted' : ''}`}>
          {secret && !show ? '••••••••' : value}
        </span>
        {secret && (
          <button onClick={() => setShow(s => !s)} aria-label={show ? `Ocultar ${label}` : `Mostrar ${label}`} className="p-1 text-muted hover:text-gray-300">
            {show ? <EyeOff size={13} /> : <Eye size={13} />}
          </button>
        )}
        <CopyBtn value={value} />
      </div>
    </div>
  );
}

/* ─── Editor (create OR edit) ───────────────────────────────── */
function ExtensionEditor({ extId, onBack }) {
  const confirm = useConfirm();
  const qc = useQueryClient();
  const isNew = !extId;
  const [tab, setTab] = useState('general');
  const [form, setForm] = useState({
    number: '', callerIdName: '', firstName: '', lastName: '',
    email: '', mobile: '', sipPassword: '', codec: 'ulaw,alaw',
    language: 'es', status: 'active', ringTimeout: 30, maxChannels: 2,
    // WebRTC por defecto: aquí todas las extensiones son softphone de navegador.
    // Con false, el token de aprovisionamiento sale como TLS/5061 y el softphone
    // no llega a registrarse (sin registro no hay escucha/susurro). Ver migración 011.
    webRtcEnabled: true, voicemailEnabled: true, dndEnabled: false,
    cfwEnabled: false, cfwNumber: '', cfwOnBusyEnabled: false, cfwBusyNumber: '',
    cfwOnNoAnswerEnabled: false, cfwNoAnswerNumber: '',
    voicemailPin: '', voicemailEmail: '', vmEmailOptions: 'notification',
    recordingEnabled: false, recordExternalOnly: false,
    sendMissedCallEmail: false, callScreening: false,
  });
  const [dirty, setDirty] = useState(false);
  // Aviso de cambios sin guardar (/impeccable critique, 30-sep-2026 — P1):
  // antes nada bloqueaba volver ni refrescar con cambios sin guardar en un
  // formulario de 7 pestañas. onBackGuarded cubre el botón de volver;
  // beforeunload cubre refrescar/cerrar la pestaña. No cubre navegar a otra
  // ruta del sidebar (requeriría un router de datos con useBlocker, que esta
  // app no usa) — limitación conocida, no resuelta aquí.
  useEffect(() => {
    if (!dirty) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);
  const onBackGuarded = async () => {
    if (dirty && !(await confirm({ title: 'Hay cambios sin guardar', message: 'Si sales ahora se pierden los cambios de esta extensión.', confirmLabel: 'Salir sin guardar', cancelLabel: 'Seguir editando', danger: true }))) return;
    onBack();
  };
  const [showPwd, setShowPwd] = useState(false);
  const [resetPwdResult, setResetPwdResult] = useState(null);
  const [showQr, setShowQr] = useState(false);
  const [qrData, setQrData] = useState(null);
  const qrRef = useRef(null);

  const { data: detail, isLoading } = useQuery(
    ['extension-detail', extId],
    () => api.get('/extensions/' + extId).then(r => r.data),
    {
      enabled: !isNew,
      refetchOnWindowFocus: false,   // no reiniciar el formulario al volver a la ventana (borraba ediciones)
    }
  );

  // Poblar el formulario cuando llegan los datos (de caché o de red).
  // Usar useEffect en vez de onSuccess: onSuccess NO se dispara si los datos
  // vienen de caché (re-editar la misma extensión) -> el form quedaba vacío y
  // al guardar borraba la info. La guarda evita pisar ediciones en curso.
  const loadedFor = useRef(null);
  useEffect(() => {
    if (isNew || !detail) return;
    if (loadedFor.current === extId) return;
    loadedFor.current = extId;
    const d = detail;
    setForm(f => ({
      ...f,
      number: d.number || '',
      callerIdName: d.caller_id_name || '',
      firstName: d.first_name || (d.caller_id_name ? d.caller_id_name.split(' ')[0] : ''),
      lastName: d.last_name || (d.caller_id_name ? d.caller_id_name.split(' ').slice(1).join(' ') : ''),
      email: d.email || d.user_email || '',
      mobile: d.mobile || '',
      sipPassword: d.sip_password || '',
      codec: d.codec || 'ulaw,alaw',
      language: d.language || 'es',
      status: d.status || 'active',
      ringTimeout: d.ring_timeout || 30,
      maxChannels: d.max_channels || 2,
      webRtcEnabled: !!d.webrtc_enabled,
      dndEnabled: !!d.dnd_enabled,
      cfwEnabled: !!d.cfw_enabled,
      cfwNumber: d.cfw_number || '',
      cfwOnBusyEnabled: !!d.cfw_on_busy_enabled,
      cfwBusyNumber: d.cfw_busy_number || '',
      cfwOnNoAnswerEnabled: !!d.cfw_on_no_answer_enabled,
      cfwNoAnswerNumber: d.cfw_no_answer_number || '',
      voicemailEnabled: d.voicemail_enabled !== false,
      voicemailPin: d.voicemail_pin || '',
      voicemailEmail: d.voicemail_email || d.email || d.user_email || '',
      vmEmailOptions: d.vm_email_options || 'notification',
      recordingEnabled: !!d.recording_enabled,
      recordExternalOnly: !!d.record_external_only,
      sendMissedCallEmail: !!d.send_missed_call_email,
      callScreening: !!d.call_screening,
    }));
  }, [detail, extId, isNew]);

  const saveMut = useMutation(
    (payload) => isNew
      ? api.post('/extensions', payload).then(r => r.data)
      : api.put('/extensions/' + extId, payload).then(r => r.data),
    {
      onSuccess: () => {
        qc.invalidateQueries('extensions');
        if (!isNew) qc.invalidateQueries(['extension-detail', extId]);
        toast.success(isNew ? 'Extensión creada' : 'Extensión guardada');
        setDirty(false);
        if (isNew) onBack();
      },
      onError: (e) => {
        const data = e.response?.data;
        let msg;
        if (data?.error) {
          msg = data.error;                         // ej. "Número de extensión ya existe" (409)
        } else if (Array.isArray(data?.errors) && data.errors.length) {
          const LABELS = {
            number: 'Número', callerIdName: 'Nombre a mostrar', sipPassword: 'Contraseña SIP',
            callerId: 'ID de llamada saliente', maxChannels: 'Canales máximos', codec: 'Códec',
          };
          msg = data.errors.map(x => `${LABELS[x.path] || x.path}: ${x.msg}`).join(' · ');
        } else {
          msg = 'Error al guardar';
        }
        toast.error(msg);
      },
    }
  );

  const resetPwdMut = useMutation(
    () => api.post('/extensions/' + extId + '/reset-password').then(r => r.data),
    {
      onSuccess: (d) => {
        setResetPwdResult(d.sipPassword);
        // El form no se repuebla solo (la guarda loadedFor lo impide),
        // así que reflejamos aquí la contraseña nueva en el input.
        setForm(f => ({ ...f, sipPassword: d.sipPassword }));
        toast.success('Contraseña SIP regenerada');
        qc.invalidateQueries(['extension-detail', extId]);
      },
      onError: () => toast.error('Error al regenerar contraseña'),
    }
  );

  const set = (key, val) => { setForm(f => ({ ...f, [key]: val })); setDirty(true); };

  const save = () => {
    const payload = {
      callerIdName: form.callerIdName || (form.firstName + ' ' + form.lastName).trim() || form.number,
      codec: form.codec,
      language: form.language,
      status: form.status,
      ringTimeout: parseInt(form.ringTimeout) || 30,
      maxChannels: parseInt(form.maxChannels) || 2,
      webRtcEnabled: form.webRtcEnabled,
      dndEnabled: form.dndEnabled,
      cfwEnabled: form.cfwEnabled,
      cfwNumber: form.cfwNumber,
      cfwOnBusyEnabled: form.cfwOnBusyEnabled,
      cfwBusyNumber: form.cfwBusyNumber,
      cfwOnNoAnswerEnabled: form.cfwOnNoAnswerEnabled,
      cfwNoAnswerNumber: form.cfwNoAnswerNumber,
      voicemailEnabled: form.voicemailEnabled,
      voicemailPin: form.voicemailPin,
      voicemailEmail: form.voicemailEmail,
      vmEmailOptions: form.vmEmailOptions,
      recordingEnabled: form.recordingEnabled,
      recordExternalOnly: form.recordExternalOnly,
      sendMissedCallEmail: form.sendMissedCallEmail,
      callScreening: form.callScreening,
      email: form.email || undefined,
      mobile: form.mobile || undefined,
    };
    if (isNew) {
      payload.number = form.number;
      payload.sipPassword = form.sipPassword;
      payload.voicemailEnabled = form.voicemailEnabled;
    } else if (form.sipPassword && form.sipPassword !== detail?.sip_password) {
      // Solo se envía si el usuario realmente la cambió: mandarla siempre
      // sobreescribiría la actual, y mandarla vacía la borraría.
      payload.sipPassword = form.sipPassword;
    }
    saveMut.mutate(payload);
  };

  const d = detail || {};
  const displayNumber = isNew ? (form.number || '—') : (d.number || form.number || '—');
  const displayName = isNew
    ? ((form.firstName + ' ' + form.lastName).trim() || 'Nueva Extensión')
    : (d.caller_id_name || d.number);

  if (!isNew && isLoading) return (
    <div className="flex items-center justify-center h-full">
      <div className="spinner" />
    </div>
  );

  return (
    <div className="flex flex-col h-full bg-gray-950">
      {/* ── QR Modal ── */}
      {showQr && qrData && (
        <QrProvisionModal data={qrData} onClose={() => setShowQr(false)} qrRef={qrRef} />
      )}

      {/* ── Top header ── */}
      <div className="flex items-center justify-between px-6 py-3.5 border-b border-gray-800 shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={onBackGuarded} aria-label="Volver" className="p-1.5 text-gray-400 hover:text-white rounded-lg hover:bg-gray-800 transition-colors">
            <ChevronLeft size={20} />
          </button>
          <h1 className="text-base font-semibold text-white">
            {isNew ? 'Nueva Extensión' : `Editar Extensión ${displayNumber}`}
          </h1>
        </div>
        <button
          onClick={save}
          disabled={saveMut.isLoading || (!dirty && !isNew)}
          className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
            (dirty || isNew) && !saveMut.isLoading
              ? 'bg-blue-600 hover:bg-blue-500 text-white'
              : 'bg-gray-800 text-muted cursor-default'
          }`}
        >
          {saveMut.isLoading ? <><div className="spinner w-3 h-3" /> Guardando...</> : '💾 Guardar'}
        </button>
      </div>

      {/* ── Tabs ── */}
      <div className="flex border-b border-gray-800 shrink-0 overflow-x-auto bg-gray-900/30">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-5 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
              tab === t.id
                ? 'border-blue-500 text-blue-400 bg-blue-600/5'
                : 'border-transparent text-muted hover:text-gray-300 hover:bg-gray-800/30'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Body: sidebar + content ── */}
      <div className="flex flex-1 overflow-hidden">

        {/* ── Left sidebar ── */}
        <div className="w-52 shrink-0 border-r border-gray-800 flex flex-col items-center pt-8 pb-6 gap-4 bg-gray-900/20">
          {/* Avatar */}
          <div className="w-20 h-20 rounded-full bg-gray-700/60 border-2 border-gray-600 flex items-center justify-center">
            <User size={36} className="text-muted" />
          </div>
          <div className="text-center px-3">
            <p className="font-mono text-lg font-bold text-white leading-none">{displayNumber}</p>
            <p className="text-xs text-muted mt-1 truncate max-w-[160px]">{displayName}</p>
          </div>

          <div className="w-full px-4 space-y-2 mt-2">
            {/* Reset Password */}
            {!isNew && (
              <button
                onClick={() => resetPwdMut.mutate()}
                disabled={resetPwdMut.isLoading}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-xs text-gray-300 transition-colors"
              >
                <RotateCcw size={13} /> Resetear Contraseña
              </button>
            )}
            {resetPwdResult && (
              <div className="p-2 bg-yellow-900/20 border border-yellow-700/40 rounded-lg">
                <p className="text-xs text-yellow-400 text-center font-mono break-all">{resetPwdResult}</p>
                <button onClick={() => { navigator.clipboard.writeText(resetPwdResult); toast.success('Copiado'); }}
                  className="w-full text-xs text-yellow-600 hover:text-yellow-400 mt-1 flex items-center justify-center gap-1">
                  <Copy size={10} /> Copiar
                </button>
              </div>
            )}

            {/* QR Code */}
            {!isNew && (
              <button
                onClick={async () => {
                  try {
                    const r = await api.post('/extensions/' + extId + '/provision-token');
                    setQrData(r.data);
                    setShowQr(true);
                  } catch {
                    toast.error('Error al generar QR');
                  }
                }}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-teal-600 hover:bg-teal-500 rounded-lg text-xs text-white transition-colors">
                <QrCode size={13} /> Código QR
              </button>
            )}

            {/* 2FA */}
            <label className="flex items-center gap-2 cursor-pointer px-1 mt-1">
              <input type="checkbox" className="w-3.5 h-3.5 accent-blue-600 rounded"
                checked={form.twoFaEnabled || false}
                onChange={e => set('twoFaEnabled', e.target.checked)} />
              <span className="text-xs text-gray-400">Habilitar 2FA</span>
            </label>
          </div>

          {/* Status badge */}
          {!isNew && (
            <div className="mt-auto px-4 w-full">
              <div className="flex items-center gap-2 p-2.5 bg-gray-800/50 rounded-lg">
                <span className={'status-dot ' + (STATUS_COLORS[d.liveStatus] || 'offline')} />
                <span className="text-xs text-gray-400">{statusLabel(d.liveStatus)}</span>
              </div>
            </div>
          )}
        </div>

        {/* ── Tab content ── */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto p-6 space-y-6">

            {/* ══ GENERAL ══ */}
            {tab === 'general' && (
              <>
                <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                  {/* Extensión */}
                  <Field label="Extensión" htmlFor="ext-number">
                    {isNew ? (
                      <input
                        id="ext-number"
                        className="input font-mono"
                        placeholder="27220"
                        value={form.number}
                        onChange={e => set('number', e.target.value)}
                      />
                    ) : (
                      <input id="ext-number" className="input bg-gray-800/30 cursor-not-allowed font-mono" value={d.number} readOnly />
                    )}
                  </Field>

                  {/* Correo electrónico */}
                  <Field label="Correo electrónico" htmlFor="ext-email">
                    <input id="ext-email" className="input" type="email" placeholder="usuario@empresa.com"
                      value={form.email} onChange={e => set('email', e.target.value)} />
                  </Field>

                  {/* Nombre */}
                  <Field label="Nombre" htmlFor="ext-first-name">
                    <input id="ext-first-name" className="input" placeholder="Juan"
                      value={form.firstName || form.callerIdName}
                      onChange={e => {
                        set('firstName', e.target.value);
                        set('callerIdName', (e.target.value + ' ' + form.lastName).trim());
                      }} />
                  </Field>

                  {/* Apellido */}
                  <Field label="Apellido" htmlFor="ext-last-name">
                    <input id="ext-last-name" className="input" placeholder="García"
                      value={form.lastName}
                      onChange={e => {
                        set('lastName', e.target.value);
                        set('callerIdName', (form.firstName + ' ' + e.target.value).trim());
                      }} />
                  </Field>

                  {/* Móvil */}
                  <Field label="Móvil" htmlFor="ext-mobile">
                    <input id="ext-mobile" className="input" type="tel" placeholder="+52 55 1234 5678"
                      value={form.mobile} onChange={e => set('mobile', e.target.value)} />
                  </Field>

                  {/* Identificador de llamada saliente */}
                  <Field label="Identificador de llamada saliente" htmlFor="ext-caller-id">
                    <div className="relative">
                      <input id="ext-caller-id" className="input pr-16" placeholder="(por defecto del sistema)"
                        value={form.callerId || ''} onChange={e => set('callerId', e.target.value)} />
                      {form.callerId && (
                        <button onClick={() => set('callerId', '')}
                          className="absolute right-8 top-1/2 -translate-y-1/2 text-muted hover:text-gray-300">
                          <X size={13} />
                        </button>
                      )}
                      <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
                    </div>
                  </Field>

                  {/* Rol */}
                  <Field label="Rol" htmlFor="ext-role">
                    <div className="relative">
                      <select id="ext-role" className="input appearance-none pr-8"
                        value={form.role || 'user'}
                        onChange={e => set('role', e.target.value)}>
                        <option value="user">Usuario</option>
                        <option value="admin">Administrador</option>
                        <option value="superadmin">Propietario</option>
                      </select>
                      <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
                    </div>
                  </Field>

                  {/* Departamento */}
                  <Field label="Membresía Principal del Departamento" htmlFor="ext-department">
                    <div className="relative">
                      <select id="ext-department" className="input appearance-none pr-8"
                        value={form.department || ''}
                        onChange={e => set('department', e.target.value)}>
                        <option value="">Sin departamento</option>
                        <option value="prime">Prime</option>
                        <option value="ventas">Ventas</option>
                        <option value="soporte">Soporte</option>
                        <option value="administracion">Administración</option>
                      </select>
                      <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
                    </div>
                  </Field>
                </div>

                {/* Número(s) DID asignado(s) - full width */}
                <Field label="Número(s) DID asignado(s)">
                  <div className="relative">
                    <select className="input appearance-none pr-8">
                      <option value="">Sin DID asignado</option>
                    </select>
                    <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
                  </div>
                </Field>

                {/* Contraseña SIP (solo en creación o como campo editable) */}
                {isNew && (
                  <Field label="Contraseña SIP *" htmlFor="ext-sip-password-new">
                    <div className="relative">
                      <input
                        id="ext-sip-password-new"
                        type={showPwd ? 'text' : 'password'}
                        className="input pr-10 font-mono"
                        placeholder="Mínimo 8 caracteres"
                        value={form.sipPassword}
                        onChange={e => set('sipPassword', e.target.value)}
                      />
                      <button type="button" onClick={() => setShowPwd(s => !s)}
                        aria-label={showPwd ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-gray-300">
                        {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                  </Field>
                )}

                {/* Credenciales SIP en edición — la contraseña es editable */}
                {!isNew && (
                  <div className="space-y-2 pt-2 border-t border-gray-800">
                    <p className="text-xs font-medium text-gray-400 mb-3">Credenciales SIP</p>
                    <SipCredRow label="Usuario SIP" value={d.number || ''} />
                    <Field label="Contraseña SIP" htmlFor="ext-sip-password-edit">
                      <div className="relative">
                        <input
                          id="ext-sip-password-edit"
                          type={showPwd ? 'text' : 'password'}
                          className="input pr-16 font-mono"
                          placeholder="Mínimo 8 caracteres"
                          value={form.sipPassword}
                          onChange={e => set('sipPassword', e.target.value)}
                        />
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
                          <button type="button" onClick={() => setShowPwd(s => !s)}
                            aria-label={showPwd ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                            className="p-1 text-muted hover:text-blue-400">
                            {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
                          </button>
                          <button type="button"
                            onClick={() => { navigator.clipboard.writeText(form.sipPassword); toast.success('Copiado'); }}
                            aria-label="Copiar contraseña SIP"
                            className="p-1 text-muted hover:text-blue-400">
                            <Copy size={14} />
                          </button>
                        </div>
                      </div>
                      {form.sipPassword !== (d.sip_password || '') && (
                        <p className="text-[11px] text-yellow-500 mt-1">
                          La extensión deberá volver a registrarse con la nueva contraseña.
                        </p>
                      )}
                    </Field>
                  </div>
                )}

                {/* Opciones avanzadas */}
                <div className="grid grid-cols-2 gap-x-6 gap-y-4 pt-2 border-t border-gray-800">
                  <Field label="Idioma" htmlFor="ext-language">
                    <select id="ext-language" className="input" value={form.language} onChange={e => set('language', e.target.value)}>
                      <option value="es">Español</option>
                      <option value="en">Inglés</option>
                      <option value="pt">Portugués</option>
                      <option value="fr">Francés</option>
                    </select>
                  </Field>
                  <Field label="Estado" htmlFor="ext-status">
                    <select id="ext-status" className="input" value={form.status} onChange={e => set('status', e.target.value)}>
                      <option value="active">Activo</option>
                      <option value="inactive">Inactivo</option>
                      <option value="suspended">Suspendido</option>
                    </select>
                  </Field>
                  <Field label="Timeout timbre (seg)" htmlFor="ext-ring-timeout">
                    <input id="ext-ring-timeout" className="input" type="number" min={5} max={120}
                      value={form.ringTimeout} onChange={e => set('ringTimeout', e.target.value)} />
                  </Field>
                  <Field label="Máx. canales simultáneos" htmlFor="ext-max-channels">
                    <input id="ext-max-channels" className="input" type="number" min={1} max={20}
                      value={form.maxChannels} onChange={e => set('maxChannels', e.target.value)} />
                  </Field>
                </div>

                <Field label="Codecs">
                  <div className="flex flex-wrap gap-2">
                    {['opus', 'g722', 'ulaw', 'alaw', 'g729'].map(c => {
                      const active = (form.codec || '').split(',').includes(c);
                      return (
                        <button key={c} type="button"
                          onClick={() => {
                            const list = (form.codec || '').split(',').filter(Boolean);
                            set('codec', active ? list.filter(x => x !== c).join(',') : [...list, c].join(','));
                          }}
                          className={`px-3 py-1.5 rounded-lg text-xs font-mono font-medium border transition-all ${
                            active ? 'bg-blue-600/20 border-blue-500 text-blue-300' : 'bg-gray-800/50 border-gray-700 text-muted hover:border-gray-500'
                          }`}>
                          {c.toUpperCase()}
                        </button>
                      );
                    })}
                  </div>
                </Field>
              </>
            )}

            {/* ══ RUTEO DE LLAMADAS ══ */}
            {tab === 'forwarding' && (
              <div className="space-y-4">
                {[
                  { label: 'Cuando disponible (siempre)', enabledKey: 'cfwEnabled', numKey: 'cfwNumber' },
                  { label: 'Cuando ocupado', enabledKey: 'cfwOnBusyEnabled', numKey: 'cfwBusyNumber' },
                  { label: 'Sin respuesta', enabledKey: 'cfwOnNoAnswerEnabled', numKey: 'cfwNoAnswerNumber' },
                ].map(rule => (
                  <div key={rule.enabledKey} className="p-4 bg-gray-800/30 rounded-xl border border-gray-800 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-200">{rule.label}</span>
                      <Toggle checked={!!form[rule.enabledKey]} onChange={v => set(rule.enabledKey, v)} />
                    </div>
                    {form[rule.enabledKey] && (
                      <Field label="Reenviar a" htmlFor={`ext-cfw-${rule.numKey}`}>
                        <input id={`ext-cfw-${rule.numKey}`} className="input" placeholder="Extensión, *vmbox o número externo"
                          value={form[rule.numKey] || ''} onChange={e => set(rule.numKey, e.target.value)} />
                      </Field>
                    )}
                  </div>
                ))}
                <p className="text-xs text-muted bg-gray-800/30 rounded-xl p-3">
                  Usa <code className="text-blue-400">*vmbox</code> para reenviar al buzón de voz, o el número de otra extensión.
                </p>
              </div>
            )}

            {/* ══ BUZÓN DE VOZ ══ */}
            {tab === 'voicemail' && (
              <div className="space-y-5">
                <Toggle checked={!!form.voicemailEnabled} onChange={v => set('voicemailEnabled', v)} label="Buzón de voz habilitado" />
                {form.voicemailEnabled && (
                  <>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                      <Field label="PIN del buzón" htmlFor="ext-vm-pin">
                        <input id="ext-vm-pin" className="input font-mono" maxLength={8} placeholder="4-8 dígitos"
                          value={form.voicemailPin} onChange={e => set('voicemailPin', e.target.value)} />
                      </Field>
                      <Field label="Email de notificación" htmlFor="ext-vm-email">
                        <input id="ext-vm-email" className="input" type="email" placeholder="correo@empresa.com"
                          value={form.voicemailEmail} onChange={e => set('voicemailEmail', e.target.value)} />
                      </Field>
                    </div>
                    <Field label="Notificación por email" htmlFor="ext-vm-email-options">
                      <select id="ext-vm-email-options" className="input" value={form.vmEmailOptions} onChange={e => set('vmEmailOptions', e.target.value)}>
                        <option value="none">Sin notificación</option>
                        <option value="notification">Solo notificación</option>
                        <option value="attachment">Con archivo adjunto</option>
                      </select>
                    </Field>
                    <p className="text-xs text-muted bg-gray-800/30 rounded-xl p-3">
                      Acceso: marcar <code className="text-blue-400">*99</code> desde esta extensión, o <code className="text-blue-400">*98{displayNumber}</code> desde otra.
                    </p>
                  </>
                )}
              </div>
            )}

            {/* ══ TELÉFONO IP ══ */}
            {tab === 'device' && (
              <div className="space-y-4">
                {!isNew && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-4 bg-gray-800/40 rounded-xl">
                      <p className="text-xs text-muted mb-1.5">Registro SIP</p>
                      <div className="flex items-center gap-2">
                        {d.liveStatus === 'Not in use' || d.liveStatus === 'In use'
                          ? <><Wifi size={15} className="text-green-400" /><span className="text-sm text-green-400 font-medium">Registrado</span></>
                          : <><WifiOff size={15} className="text-muted" /><span className="text-sm text-gray-400">No registrado</span></>
                        }
                      </div>
                    </div>
                    <div className="p-4 bg-gray-800/40 rounded-xl">
                      <p className="text-xs text-muted mb-1.5">Estado en tiempo real</p>
                      <div className="flex items-center gap-2">
                        <span className={'status-dot ' + (STATUS_COLORS[d.liveStatus] || 'offline')} />
                        <span className="text-sm text-gray-200">{statusLabel(d.liveStatus)}</span>
                      </div>
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  {[
                    ['Servidor SIP / Proxy', window.location.hostname],
                    ['Puerto SIP', '5060 (UDP/TCP)'],
                    ['WebSocket', '8088 (WS)'],
                    ['Usuario / Auth ID', displayNumber],
                    ['Transporte', form.webRtcEnabled ? 'WS/WSS' : 'UDP/TCP'],
                  ].map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between px-3 py-2.5 bg-gray-800/40 rounded-lg">
                      <span className="text-xs text-muted">{k}</span>
                      <span className="text-xs font-mono text-gray-200">{v}</span>
                    </div>
                  ))}
                </div>
                {!isNew && d.sip_password && (
                  <SipCredRow label="Contraseña SIP" value={d.sip_password} />
                )}
                <Toggle checked={!!form.webRtcEnabled} onChange={v => set('webRtcEnabled', v)} label="WebRTC habilitado (softphone en navegador)" />
              </div>
            )}

            {/* ══ BLF ══ */}
            {tab === 'blf' && (
              <div className="flex flex-col items-center justify-center py-12 text-center gap-3">
                <Monitor size={32} className="text-gray-700" />
                <p className="text-sm text-muted">Configuración BLF</p>
                <p className="text-xs text-muted max-w-xs">
                  Las teclas de función (BLF) se configuran directamente en el teléfono IP. Consulta la documentación de tu dispositivo.
                </p>
              </div>
            )}

            {/* ══ VISTA ══ */}
            {tab === 'vista' && !isNew && (
              <VistaTab extId={extId} initialPerms={detail?.group_permissions} />
            )}
            {tab === 'vista' && isNew && (
              <p className="text-sm text-muted text-center py-12">Guarda la extensión primero para configurar permisos de Vista.</p>
            )}

            {/* ══ OPCIONES ══ */}
            {tab === 'options' && (
              <div className="space-y-4">
                <Toggle checked={!!form.dndEnabled} onChange={v => set('dndEnabled', v)} label="No molestar (DND) — rechaza todas las llamadas entrantes" />
                <Toggle checked={!!form.sendMissedCallEmail} onChange={v => set('sendMissedCallEmail', v)} label="Notificación por email en llamadas perdidas" />
                <Toggle checked={!!form.callScreening} onChange={v => set('callScreening', v)} label="Filtrado de llamadas (pedir nombre antes de conectar)" />
                <Toggle checked={!!form.recordingEnabled} onChange={v => set('recordingEnabled', v)} label="Grabar llamadas" />
                {form.recordingEnabled && (
                  <div className="pl-6 space-y-3">
                    <Toggle checked={!!form.recordExternalOnly} onChange={v => set('recordExternalOnly', v)} label="Solo llamadas externas (no internas)" />
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Vista tab (permisos de grupo) ─────────────────────────── */
function VistaTab({ extId, initialPerms }) {
  const qc = useQueryClient();
  const [perms, setPerms] = useState(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (initialPerms?.length) setPerms(initialPerms[0]);
  }, [initialPerms]);

  const saveMut = useMutation(
    (payload) => api.put('/extensions/' + extId + '/group-permissions', payload).then(r => r.data),
    {
      onSuccess: (data) => { setPerms(data); setDirty(false); toast.success('Permisos guardados'); },
      onError: () => toast.error('Error al guardar permisos'),
    }
  );

  if (!perms) return (
    <p className="text-sm text-muted text-center py-12">Sin configuración de grupo asignada.</p>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted">Permisos de visibilidad — departamento <strong className="text-gray-300">{perms.group_name}</strong></p>
        {dirty && (
          <button onClick={() => saveMut.mutate({
            groupName: perms.group_name,
            showMyCalls: perms.show_my_calls,
            bargeListenWhisper: perms.barge_listen_whisper,
            viewPresence: perms.view_presence,
            viewCalls: perms.view_calls,
            callOperations: perms.call_operations,
            userOperations: perms.user_operations,
          })} className="btn-primary text-xs px-3 py-1.5">Guardar Vista</button>
        )}
      </div>
      <div className="overflow-x-auto rounded-xl border border-gray-800">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-800 bg-gray-900/60">
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-400">Departamento</th>
              {VISTA_COLUMNS.map(col => (
                <th key={col.key} className="px-3 py-3 text-center">
                  <span className="text-xs font-medium text-gray-400 whitespace-nowrap flex items-center justify-center gap-1">
                    {col.label} <InfoTooltipSmall text={col.desc} />
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr className="hover:bg-gray-800/30">
              <td className="px-4 py-3 text-sm text-gray-200 font-medium">{perms.group_name}</td>
              {VISTA_COLUMNS.map(col => (
                <td key={col.key} className="px-3 py-3 text-center">
                  <button
                    onClick={() => { setPerms(p => ({ ...p, [col.key]: !p[col.key] })); setDirty(true); }}
                    aria-label={`${col.label} para ${perms.group_name}`} aria-pressed={!!perms[col.key]}
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center mx-auto transition-all ${
                      perms[col.key] ? 'bg-blue-600 border-blue-500' : 'bg-transparent border-gray-600 hover:border-gray-400'
                    }`}>
                    {perms[col.key] && (
                      <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                        <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </button>
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function InfoTooltipSmall({ text }) {
  const [show, setShow] = useState(false);
  return (
    <span className="relative inline-flex">
      <button type="button" aria-label="Más información" className="text-muted hover:text-gray-400"
        onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
        <AlertCircle size={11} />
      </button>
      {show && (
        <span className="absolute z-50 left-4 top-0 w-52 p-2.5 bg-gray-800 border border-gray-700 rounded-lg text-xs text-gray-300 shadow-xl pointer-events-none">
          {text}
        </span>
      )}
    </span>
  );
}

/* ─── Extensions list page ───────────────────────────────────── */
export default function ExtensionsPage() {
  const confirm = useConfirm();
  const qc = useQueryClient();
  const { user } = useAuthStore();
  const [search, setSearch] = useState('');
  const [view, setView] = useState({ mode: 'list' }); // { mode: 'list' | 'edit' | 'new', extId? }
  const [ctxMenu, setCtxMenu] = useState(null); // { x, y, ext } — menú de monitoreo (clic derecho)

  // Espía/susurro/irrupción sobre una extensión en llamada (usa prefijo de canal PJSIP/<num>)
  const doMonitor = async (extNumber, mode) => {
    setCtxMenu(null);
    const supervisorExt = user?.extensionNumber;
    if (!supervisorExt) { toast.error('Tu usuario no tiene una extensión asignada para supervisar'); return; }
    try {
      await api.post('/monitor/spy', { supervisorExt, targetChannel: `PJSIP/${extNumber}`, mode });
      const labels = { listen: 'Escucha iniciada', whisper: 'Susurro iniciado', barge: 'Irrumpiendo en la llamada' };
      toast.success(`${labels[mode] || 'Monitoreo iniciado'} — sonará tu extensión ${supervisorExt}`);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'No se pudo iniciar el monitoreo');
    }
  };

  // Cerrar el menú al hacer clic en cualquier parte
  useEffect(() => {
    if (!ctxMenu) return undefined;
    const close = () => setCtxMenu(null);
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    return () => { window.removeEventListener('click', close); window.removeEventListener('scroll', close, true); };
  }, [ctxMenu]);

  const { data, isLoading } = useQuery(
    ['extensions', search],
    () => api.get('/extensions?search=' + search + '&limit=100').then(r => r.data),
    { keepPreviousData: true }
  );

  const deleteMut = useMutation(
    (id) => api.delete('/extensions/' + id),
    {
      onSuccess: () => { qc.invalidateQueries('extensions'); toast.success('Extensión eliminada'); },
      onError: (e) => toast.error(e.response?.data?.error || 'Error'),
    }
  );

  const extensions = data?.data || [];

  /* Show editor */
  if (view.mode === 'edit' || view.mode === 'new') {
    return (
      <div className="h-full -m-6">
        <ExtensionEditor
          extId={view.mode === 'edit' ? view.extId : null}
          onBack={() => setView({ mode: 'list' })}
        />
      </div>
    );
  }

  /* List view */
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-white">Extensiones</h1>
          <p className="text-muted text-xs mt-0.5">{data?.total || 0} extensiones configuradas</p>
        </div>
        <button onClick={() => setView({ mode: 'new' })} className="btn-primary">
          <Plus size={15} /> Nueva Extensión
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-xs">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
        <label htmlFor="ext-search" className="sr-only">Buscar extensión o nombre</label>
        <input id="ext-search" value={search} onChange={e => setSearch(e.target.value)}
          className="input pl-8 text-sm" placeholder="Buscar extensión o nombre..." />
      </div>

      {/* Table */}
      <div className="card overflow-hidden p-0">
        {isLoading ? (
          <div className="flex justify-center py-16"><div className="spinner" /></div>
        ) : extensions.length === 0 ? (
          <div className="text-center py-16 text-muted text-sm">
            <Phone size={32} className="mx-auto mb-3 text-gray-800" />
            Sin extensiones. Crea la primera.
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                {['Extensión', 'Nombre', 'Email', 'Estado SIP', 'Presencia', 'WebRTC', 'Acciones'].map(h => (
                  <th key={h} className="table-th text-xs">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {extensions.map(ext => {
                const presOpt = PRESENCE_OPTIONS.find(o => o.value === (ext.presence_status || 'available')) || PRESENCE_OPTIONS[0];
                const PresIcon = presOpt.icon;
                return (
                  <tr key={ext.id} className="table-row cursor-pointer"
                    onClick={() => setView({ mode: 'edit', extId: ext.id })}
                    onContextMenu={(e) => {
                      if (ext.liveStatus === 'In use' || ext.liveStatus === 'Ringing') {
                        e.preventDefault();
                        setCtxMenu({ x: e.clientX, y: e.clientY, ext });
                      }
                    }}>
                    <td className="table-td">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 bg-blue-600/15 rounded-lg flex items-center justify-center shrink-0">
                          <Phone size={13} className="text-blue-400" />
                        </div>
                        <span className="font-mono font-bold text-white">{ext.number}</span>
                      </div>
                    </td>
                    <td className="table-td text-gray-200 text-sm">{ext.caller_id_name}</td>
                    <td className="table-td">
                      <span className="text-xs text-muted truncate max-w-[160px] block">{ext.email || '—'}</span>
                    </td>
                    <td className="table-td">
                      <div className="flex items-center gap-1.5">
                        <span className={'status-dot ' + (STATUS_COLORS[ext.liveStatus] || 'offline')} />
                        <span className="text-xs text-gray-400">{statusLabel(ext.liveStatus)}</span>
                      </div>
                    </td>
                    <td className="table-td">
                      <span className={`flex items-center gap-1 text-xs ${presOpt.color}`}>
                        <PresIcon size={11} /> {presOpt.label}
                      </span>
                    </td>
                    <td className="table-td">
                      <span className={ext.webrtc_enabled ? 'badge-success text-xs' : 'text-xs text-muted'}>
                        {ext.webrtc_enabled ? 'Sí' : 'No'}
                      </span>
                    </td>
                    <td className="table-td" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        <button onClick={() => setView({ mode: 'edit', extId: ext.id })}
                          aria-label={`Editar extensión ${ext.number}`}
                          className="p-1.5 text-muted hover:text-blue-400 rounded-lg transition-colors">
                          <Settings size={14} />
                        </button>
                        <button onClick={async () => {
                          if (extensionHasActiveCall(ext.liveStatus)) {
                            toast.error(`La extensión ${ext.number} tiene una llamada en curso — no se puede eliminar ahora.`);
                            return;
                          }
                          if (await confirm({ title: `Eliminar la extensión ${ext.number}`, message: 'Esta acción no se puede deshacer.', confirmLabel: 'Eliminar', danger: true })) deleteMut.mutate(ext.id);
                        }}
                          aria-label={`Eliminar extensión ${ext.number}`}
                          className="p-1.5 text-muted hover:text-red-400 rounded-lg transition-colors">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Menú contextual de monitoreo (clic derecho sobre extensión en llamada) */}
      {ctxMenu && (
        <div
          className="fixed z-50 min-w-[190px] bg-gray-800 border border-gray-700 rounded-lg shadow-2xl py-1 text-sm"
          style={{ top: ctxMenu.y, left: ctxMenu.x }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-3 py-1.5 text-xs text-muted border-b border-gray-700/60">
            Ext. {ctxMenu.ext.number} · en llamada
          </div>
          <button onClick={() => doMonitor(ctxMenu.ext.number, 'listen')}
            className="w-full flex items-center gap-2 px-3 py-2 text-gray-200 hover:bg-gray-700/60 text-left">
            <Eye size={14} className="text-blue-400" /> Escuchar
          </button>
          <button onClick={() => doMonitor(ctxMenu.ext.number, 'whisper')}
            className="w-full flex items-center gap-2 px-3 py-2 text-gray-200 hover:bg-gray-700/60 text-left">
            <Mic size={14} className="text-amber-400" /> Susurrar
          </button>
          <button onClick={() => doMonitor(ctxMenu.ext.number, 'barge')}
            className="w-full flex items-center gap-2 px-3 py-2 text-gray-200 hover:bg-gray-700/60 text-left">
            <Radio size={14} className="text-red-400" /> Irrumpir
          </button>
        </div>
      )}
    </div>
  );
}
